import axios, { pluckData } from 'src/utilities/http-client'
import {
  PlanningCourseV2,
  PlanningCourseV2Details,
  CourseV2Compliance,
  ContentComplianceStatus,
  CourseV2Progress,
  CourseV2PublishReadiness,
  CourseMigratableContent,
  Instructor,
  Objective,
  Reference,
  TextContent,
} from 'src/v3/core/types'

// ─── Core Course ─────────────────────────────────────────────────────────────

export let fetchPlanningCoursesV2 = () =>
  axios
    .get<PlanningCourseV2[]>('/api/pipeline/planning/courses')
    .then(pluckData)

export let fetchPlanningCourseV2Details = (courseId: string) =>
  axios
    .get<PlanningCourseV2Details>(`/api/pipeline/planning/courses/${courseId}`)
    .then(pluckData)

export let createPlanningCourseV2 = (payload: {
  sectionID: string
  templateID: string
  name: string
  length: number
}) =>
  axios
    .post<PlanningCourseV2>('/api/pipeline/planning/courses', payload)
    .then(pluckData)

export let updatePlanningCourseV2Core = (
  courseId: string,
  payload: {
    name: string
    sectionID: string
    templateID: string
    length: number
  },
) =>
  axios
    .patch(`/api/pipeline/planning/courses/${courseId}/core`, payload)
    .then(pluckData)

export let fetchCourseV2PublishReadiness = (courseId: string) =>
  axios
    .get<CourseV2PublishReadiness>(
      `/api/pipeline/planning/courses/${courseId}/publish-readiness`,
    )
    .then(pluckData)

export let updatePlanningCourseV2Publish = (
  courseId: string,
  newPublicationStatus: 'published' | 'unpublished',
) =>
  axios
    .put(`/api/pipeline/planning/courses/${courseId}/publish`, {
      newPublicationStatus,
    })
    .then(pluckData)

export let deletePlanningCourseV2 = (courseId: string) =>
  axios.delete(`/api/pipeline/planning/courses/${courseId}`).then(pluckData)

export let updateCourseV2Image = (params: {
  id: string
  type: 'Banner' | 'Thumbnail'
  contentType: string
}) =>
  axios
    .put<string>('/api/pipeline/planning/courses/image', params)
    .then(pluckData)

// ─── Syllabus ─────────────────────────────────────────────────────────────────

export let updatePlanningCourseV2Syllabus = (
  courseId: string,
  payload: {
    description?: string | null
    practiceGap?: string | null
    practiceGapEvidence?: string | null
    learnerEngagement?: string | null
    learningOutcomes?: string | null
  },
) =>
  axios
    .put(`/api/pipeline/planning/courses/${courseId}/syllabus`, payload)
    .then(pluckData)

// ─── Objectives ───────────────────────────────────────────────────────────────

export let fetchPlanningCourseV2Objectives = (courseId: string) =>
  axios
    .get<Objective[]>(`/api/pipeline/planning/courses/${courseId}/objectives`)
    .then(pluckData)

export let updatePlanningCourseV2Objectives = (
  courseId: string,
  objectives: { text: string }[],
) =>
  axios
    .put<Objective[]>(`/api/pipeline/planning/courses/${courseId}/objectives`, {
      objectives,
    })
    .then(pluckData)

// ─── References ───────────────────────────────────────────────────────────────

export let fetchPlanningCourseV2References = (courseId: string) =>
  axios
    .get<Reference[]>(`/api/pipeline/planning/courses/${courseId}/references`)
    .then(pluckData)

export let updatePlanningCourseV2References = (
  courseId: string,
  references: { text: string }[],
) =>
  axios
    .put<Reference[]>(`/api/pipeline/planning/courses/${courseId}/references`, {
      references,
    })
    .then(pluckData)

// ─── Instructors ──────────────────────────────────────────────────────────────

export let fetchPlanningCourseV2AvailableInstructors = () =>
  axios
    .get<Instructor[]>('/api/pipeline/planning/courses/instructors')
    .then(pluckData)

export let createPlanningCourseV2Instructor = (payload: {
  name: string
  credentials?: string | null
}) =>
  axios
    .post<Instructor>('/api/pipeline/planning/courses/instructor', payload)
    .then(pluckData)

export let updatePlanningCourseV2Instructor = (
  instructorId: string,
  payload: {
    name: string
    credentials?: string | null
  },
) =>
  axios
    .put<Instructor>(
      `/api/pipeline/planning/courses/instructor/${instructorId}`,
      payload,
    )
    .then(pluckData)

export let updatePlanningCourseV2Instructors = (
  courseId: string,
  instructors: { instructorID: string }[],
) =>
  axios
    .put<Instructor[]>(
      `/api/pipeline/planning/courses/${courseId}/instructors`,
      { instructors },
    )
    .then(pluckData)

// ─── Sections & Templates ─────────────────────────────────────────────────────

export let fetchPlanningSectionsAPI = () =>
  axios.get('/api/pipeline/planning/courses/sections').then(pluckData)

export let fetchPlanningTemplatesAPI = () =>
  axios.get('/api/pipeline/planning/courses/templates').then(pluckData)

// ─── Migrations ──────────────────────────────────────────────────────────────

export let fetchCourseMigratableContent = (courseID: string) =>
  axios
    .get<CourseMigratableContent>(
      `/api/pipeline/course-v2/${courseID}/migratable-content`,
    )
    .then(pluckData)

export let migrateCourseV2Quiz = (courseID: string) =>
  axios
    .post<string>(`/api/pipeline/course-v2/${courseID}/migrate/quiz`)
    .then(pluckData)

export let migrateCourseV2Accreditation = (courseID: string) =>
  axios
    .post<string>(`/api/pipeline/course-v2/${courseID}/migrate/accreditation`)
    .then(pluckData)

export let migrateCourseV2Video = (courseID: string, videoID: string) =>
  axios
    .post(`/api/pipeline/course-v2/${courseID}/migrate/video`, { videoID })
    .then(pluckData)

export let migrateCourseV2ShowNote = (courseID: string, showNotesID: string) =>
  axios
    .post(`/api/pipeline/course-v2/${courseID}/migrate/show-notes`, {
      showNotesID,
    })
    .then(pluckData)

// ─── CAPCE Accreditation ──────────────────────────────────────────────────────

export let fetchCourseV2CAPCE = (courseID: string) =>
  axios
    .get<import('src/v3/core/types').CAPCEAccreditationV2>(
      `/api/pipeline/planning/courses/${courseID}/capce`,
    )
    .then(pluckData)

export let createCourseV2CAPCE = (
  courseID: string,
  payload: {
    courseCode: string
    expirationDate: string
    applicationDate?: string | null
    submissionDate?: string | null
    topicArea: string
    classType: string
    allClearDate?: string | null
  },
) =>
  axios
    .post<import('src/v3/core/types').CAPCEAccreditationV2>(
      `/api/pipeline/planning/courses/${courseID}/capce`,
      payload,
    )
    .then(pluckData)

export let updateCourseV2CAPCE = (
  courseID: string,
  payload: {
    id: string
    courseCode?: string | null
    expirationDate?: string | null
    applicationDate?: string | null
    submissionDate?: string | null
    topicArea?: string | null
    classType?: string | null
    allClearDate?: string | null
  },
) =>
  axios
    .put<import('src/v3/core/types').CAPCEAccreditationV2>(
      `/api/pipeline/planning/courses/${courseID}/capce`,
      payload,
    )
    .then(pluckData)

export let deleteCourseV2CAPCE = (courseID: string) =>
  axios
    .delete(`/api/pipeline/planning/courses/${courseID}/capce`)
    .then(pluckData)

// ─── ANCC Accreditation ───────────────────────────────────────────────────────

export let fetchCourseV2ANCC = (courseID: string) =>
  axios
    .get<import('src/v3/core/types').ANCCAccreditation>(
      `/api/pipeline/planning/courses/${courseID}/ancc`,
    )
    .then(pluckData)

export let createCourseV2ANCC = (
  courseID: string,
  payload: {
    courseCode: string
    expirationDate: string
    targetAudience: string[]
    educationalNeeds: string[]
    competency?: string | null
    professionalSource?: string | null
  },
) =>
  axios
    .post<import('src/v3/core/types').ANCCAccreditation>(
      `/api/pipeline/planning/courses/${courseID}/ancc`,
      payload,
    )
    .then(pluckData)

export let updateCourseV2ANCC = (
  courseID: string,
  payload: {
    id: string
    courseCode?: string | null
    expirationDate?: string | null
    targetAudience?: string[] | null
    educationalNeeds?: string[] | null
    competency?: string | null
    professionalSource?: string | null
  },
) =>
  axios
    .put<import('src/v3/core/types').ANCCAccreditation>(
      `/api/pipeline/planning/courses/${courseID}/ancc`,
      payload,
    )
    .then(pluckData)

export let deleteCourseV2ANCC = (courseID: string) =>
  axios
    .delete(`/api/pipeline/planning/courses/${courseID}/ancc`)
    .then(pluckData)

// ─── Quiz ─────────────────────────────────────────────────────────────────────

export let fetchCourseV2Quiz = (courseID: string) =>
  axios
    .get<import('src/v3/core/types').QuizV2>(
      `/api/pipeline/planning/courses/${courseID}/quiz`,
    )
    .then(pluckData)

export let fetchCourseV2QuizQuestions = (courseID: string) =>
  axios
    .get<import('src/v3/core/types').QuestionData[]>(
      `/api/pipeline/planning/courses/${courseID}/quiz/questions`,
    )
    .then(pluckData)

export let createCourseV2Quiz = (payload: {
  courseID: string
  passingScore: number
  displayedQuestions: number
  randomizeQuestions: boolean
}) =>
  axios
    .post<import('src/v3/core/types').QuizV2>(
      '/api/pipeline/planning/courses/quiz',
      payload,
    )
    .then(pluckData)

// ─── Compliance ───────────────────────────────────────────────────────────────

export let fetchCourseV2Compliances = () =>
  axios
    .get<CourseV2Compliance[]>('/api/pipeline/course-compliances')
    .then(pluckData)

export let createCourseV2ComplianceEvent = (params: {
  courseID: string
  field:
    | 'Name'
    | 'Description'
    | 'Hours'
    | 'Thumbnail'
    | 'Banner'
    | 'Template'
    | 'Section'
    | 'PracticeGap'
    | 'PracticeGapEvidence'
    | 'LearnerEngagement'
    | 'LearningOutcomes'
    | 'Objectives'
    | 'References'
    | 'Instructors'
    | 'SyllabusDescription'
    | 'Content'
    | 'Quiz'
    | 'Capce'
    | 'Ancc'
  status: ContentComplianceStatus
  complianceFeedback?: string | null
}) => axios.post('/api/pipeline/compliance-course', params).then(pluckData)

// ─── Progress ─────────────────────────────────────────────────────────────────

export let fetchPlanningCoursesV2Progress = () =>
  axios
    .get<{
      courses: CourseV2Progress[]
    }>('/api/pipeline/planning/courses/progress')
    .then(pluckData)

// ─── Show Notes (Content Tab) ─────────────────────────────────────────────────

export type PipelineShowNotes = {
  id: string
  linkedServiceID: string | null
  classID: string | null
  name: string
  description: string | null
  createdTime: string
  updatedTime: string
}

export let fetchCourseV2LegacyTextContent = (courseId: string) =>
  axios
    .get<TextContent[]>(
      `/api/pipeline/planning/courses/${courseId}/legacy/text-content`,
    )
    .then(pluckData)

export let fetchCourseV2ShowNotes = (courseId: string) =>
  axios
    .get<PipelineShowNotes[]>(
      `/api/pipeline/planning/courses/${courseId}/show-notes`,
    )
    .then(pluckData)

// Creates a new ShowNotes record, CourseShowNotes join, promotes course to v2 if
// needed, and emits a Content / Updated compliance event.
export let createCourseShowNotes = (
  courseId: string,
  payload: { name: string; description: string | null },
) =>
  axios
    .post<PipelineShowNotes>(
      `/api/pipeline/planning/courses/${courseId}/migrate/text-content`,
      payload,
    )
    .then(pluckData)

export let deleteCourseShowNotes = (showNotesId: string) =>
  axios
    .delete(`/api/pipeline/planning/courses/show-notes/${showNotesId}`)
    .then(pluckData)

export let updateCourseShowNotes = (
  showNotesID: string,
  payload: { name: string; description: string | null },
) =>
  axios
    .put<PipelineShowNotes>(
      `/api/pipeline/planning/courses/show-notes/${showNotesID}`,
      payload,
    )
    .then(pluckData)

// ─── Content Steps ────────────────────────────────────────────────────────────

export let updateCourseV2ContentSteps = (
  courseId: string,
  steps: {
    type: 'Video' | 'ShowNotes'
    videoID: string | null
    showNotesID: string | null
  }[],
) =>
  axios
    .put<string>(`/api/pipeline/planning/courses/${courseId}/content/steps`, {
      steps,
    })
    .then(pluckData)

// ─── Videos (Content Tab) ─────────────────────────────────────────────────────

export type PipelineVideo = {
  id: string
  linkedServiceID: string | null
  classID: string | null
  name: string
  description: string
  fileName: string
  fileType: string
  createdTime: string
  updatedTime: string
}

export let fetchCourseV2Videos = (courseId: string) =>
  axios
    .get<PipelineVideo[]>(`/api/pipeline/planning/courses/${courseId}/videos`)
    .then(pluckData)

export let createCourseVideo = (
  courseId: string,
  payload: { name: string; description: string; key: string },
) =>
  axios
    .post<PipelineVideo>(
      `/api/pipeline/planning/courses/${courseId}/videos`,
      payload,
    )
    .then(pluckData)

export let updateCourseVideo = (
  videoId: string,
  payload: { name: string; description: string },
) =>
  axios
    .put<PipelineVideo>(
      `/api/pipeline/planning/courses/videos/${videoId}`,
      payload,
    )
    .then(pluckData)

export let updateCourseVideoFile = (videoId: string, fileName: string) =>
  axios
    .put(`/api/pipeline/planning/courses/videos/${videoId}/file`, { fileName })
    .then(pluckData)

export let deleteCourseVideo = (videoId: string) =>
  axios
    .delete(`/api/pipeline/planning/courses/videos/${videoId}`)
    .then(pluckData)

export let fetchCourseVideoDownloadUrl = (videoId: string) =>
  axios
    .get<string>(`/api/pipeline/planning/courses/videos/${videoId}/download`)
    .then(pluckData)

// ─── Video Transcripts ────────────────────────────────────────────────────────

export let fetchCourseVideoTranscript = (videoId: string) =>
  axios
    .get<string>(`/api/pipeline/planning/courses/videos/${videoId}/transcript`)
    .then(pluckData)

export let fetchCourseVideoTranscriptCorrected = (videoId: string) =>
  axios
    .get<string>(
      `/api/pipeline/planning/courses/videos/${videoId}/transcript/corrected`,
    )
    .then(pluckData)

export let fetchCourseVideoTranscriptPutUrls = (videoId: string) =>
  axios
    .get<{
      transcriptUploadURL: string
      vttUploadURL: string
    }>(`/api/pipeline/planning/courses/videos/${videoId}/transcript/put`)
    .then(pluckData)

export let triggerCourseVideoTranscription = (videoId: string) =>
  axios
    .post<{
      jobName: string
      status: string
    }>(`/api/pipeline/planning/courses/videos/${videoId}/transcript/trigger`)
    .then(pluckData)

// ─── Video Vocabulary ─────────────────────────────────────────────────────────

export type VocabularyTerm = {
  phrase: string
  ipa: string | null
  soundsLike: string | null
  displayAs: string
}

export type VocabularyResponse = {
  vocabularyName: string
  vocabularyState: string
  terms: VocabularyTerm[]
}

export let fetchVideoVocabulary = () =>
  axios
    .get<VocabularyResponse>('/api/pipeline/planning/video/vocabulary')
    .then(pluckData)

export let addVideoVocabularyTerm = (payload: {
  phrase: string
  soundsLike?: string | null
  ipa?: string | null
  displayAs?: string
}) =>
  axios.post('/api/pipeline/planning/video/vocabulary', payload).then(pluckData)

export let deleteVideoVocabularyTerm = (phrase: string) =>
  axios
    .delete(
      `/api/pipeline/planning/video/vocabulary?phrase=${encodeURIComponent(phrase)}`,
    )
    .then(pluckData)

export type VideoReprobeResult = {
  videoID: string
  lengthInMs: number | null
  error: string | null
}

export type VideoReprobeLengthResponse = {
  results: VideoReprobeResult[]
  processedCount: number
  remainingCount: number
  nextCursor: string | null
  hasMore: boolean
}

export let backfillVideoLengths = (afterID?: string) =>
  axios
    .post<VideoReprobeLengthResponse>(
      '/api/pipeline/planning/video/reprobe-length',
      null,
      { params: { limit: 25, afterID } },
    )
    .then(pluckData)
